/** @format */

import React, { useRef, useEffect, useState } from "react";
import { ReactComponent as CloseIcon } from "images/svg/close.svg";
import DatePicker from "react-datepicker";
import axios_instance from "store/constant/axiosInstance";
import { toast } from "react-toastify";
import { extractErrorMessage } from "utils/strings";

const ScoresModal = ({ showModal, setShowModal, record=null, source = "group" }) => {
  const modalRef = useRef(null);
  const [form, setForm] = useState({
    loading: false,
  });

  const [date, setDate] = useState({
    start: null,
    end: null,
  });
  const [error, setError] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);

  const modalStyle = {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 1001,
    width: "350px",
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setShowModal(false);
      }
    };

    if (showModal) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showModal, setShowModal]);

  if (!showModal) return null;

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    const allowedFileTypes = [
      // "application/vnd.ms-excel",
      // "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/csv",
    ];

    if (file) {
      const fileType = file.type;
      if (allowedFileTypes.includes(fileType)) {
        setSelectedFile(file);
      } else if (file.size > 10 * 1024 * 1024) {
        toast.error("❌ File size exceeds the 2MB limit.!", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
        });
        setSelectedFile(null);
      } else {
        toast.error("❌ Only csv files are allowed. !", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
        });
        setSelectedFile(null);
      }
    }
  };

  const handleFetchScores = async () => {
    const formattedDate = date.start.toISOString().split("T")[0];
    const formattedEndDate = date?.end
      ? date.end.toISOString().split("T")[0]
      : date.start.toISOString().split("T")[0];

    try {
      setForm({ ...form, loading: true });

      const formData = new FormData();
      formData.append("start_date", formattedDate);
      formData.append("end_date", date?.end ? formattedEndDate : formattedDate);

      if (source === "group") {
        formData.append("data_type", "group");
        formData.append("group_id", record?.id);
      }

      if (source === "file") {
        formData.append("data_type", "file");
        formData.append("file", selectedFile);
      }

      await axios_instance.post(
        "leaderboard/admin/fellows/fetch-scores",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      toast.success(`✔️ Scores retrieved successfully!`, {
        position: "top-right",
        autoClose: 1500,
        hideProgressBar: false,
        closeOnClick: true,
      });
      setForm({ ...form, loading: false });
      setShowModal(false);
    } catch (error) {
                 const formattedError = extractErrorMessage(error.response)
     
      toast.error(`❌  ${formattedError || 'Error retrieving scores!' }`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
      });
      setForm({ ...form, loading: false });
      // setShowModal(false);
    }
  };

  const isValidDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return true;

    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays <= 1;
  };

  const getMaxEndDate = (startDate) => {
    if (!startDate) return null;

    const maxDate = new Date(startDate);
    maxDate.setDate(maxDate.getDate() + 1);

    return maxDate;
  };

  const handleStartDateChange = (selectedDate) => {
    const newDate = { ...date, start: selectedDate };

    if (newDate.end && !isValidDateRange(selectedDate, newDate.end)) {
      newDate.end = null;
      setError("");
    }

    setDate(newDate);
  };

  const handleEndDateChange = (selectedDate) => {
    const newDate = { ...date, end: selectedDate };

    if (!isValidDateRange(newDate.start, selectedDate)) {
      setError("Date range cannot exceed 2 days");
      return;
    }

    setError("");
    setDate(newDate);
  };

  const validation = !date.start || (source === "file" && !selectedFile) || error;

  return (
    <>
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 1000,
        }}
      />
      <div className="group-members-modal" style={modalStyle} ref={modalRef}>
        <>
          <div className="d-flex align-items-center w-100 justify-content-between mb-3">
            <h4>Fetch Group Scores</h4>
            <div className="filter-modal-unavailable-close">
              <CloseIcon onClick={() => setShowModal(false)} />
            </div>
          </div>

          <div className="filter-modal-content">
            <div className="filter-section">
              <div className="filter-group">
                {source === "file" && (
                  <div className="mb-3">
                    <label htmlFor="progressFile" className="form-label mb-2">
                      Upload Progress Sheet (Excel or CSV)
                    </label>
                    <input
                      className="form-control"
                      type="file"
                      id="progressFile"
                      accept=".csv"
                      onChange={handleFileChange}
                    />
                  </div>
                )}

                <div className="d-flex">
                  <div className="me-2">
                    <label>Start</label>
                    <div className="score-range-container calendar-input">
                      <DatePicker
                        className="form-control bg-transparent w-full w-100"
                        selected={date.start}
                        onChange={handleStartDateChange}
                        placeholder="Start Date"
                        showIcon
                      />
                    </div>
                  </div>

                  <div className="ms-2">
                    <label>End</label>
                    <DatePicker
                      selected={date.end}
                      onChange={handleEndDateChange}
                      placeholder="End Date"
                      showIcon
                      className="form-control bg-transparent w-full w-100"
                      minDate={date.start} // Prevent selecting dates before start date
                      maxDate={getMaxEndDate(date.start)} // Limit to max 2 days from start
                      disabled={!date.start}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="filter-section">
              <div className="filter-group">
                <div className="score-range-container">
                  <button
                    className="reset-filter"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="show-results-button"
                    disabled={validation}
                    onClick={() => {
                      handleFetchScores();
                    }}
                  >
                    {form?.loading ? (
                      <span
                        role="status"
                        aria-hidden="true"
                        className="spinner-border spinner-border-sm ms-2"
                      ></span>
                    ) : (
                      "Submit"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      </div>
    </>
  );
};

export default ScoresModal;
